import React, { ComponentType, ReactNode } from 'react'
import { ChevronLeft, ChevronRight } from 'react-feather'
import { ListMeta } from 'types'
import Link from 'next/link'
import { urlFor } from './scheduleSearch/urlFor'
import { SearchParams } from 'api/types'

const PaginationItem: ComponentType<{
  active?: boolean
  disabled?: boolean
  href?: string
  children?: ReactNode
}> = ({ children, active = false, disabled = false, href }) => (
  <div
    className={`pagination-item ${active ? 'active' : ''} ${
      disabled ? 'disabled' : ''
    }`}
  >
    {disabled ? (
      <span>{children}</span>
    ) : (
      <Link href={href || ''}>{children}</Link>
    )}
  </div>
)

export const defaultMeta: ListMeta = {
  page: 1,
  facets: [],
  pageCount: 1,
  perPage: 1,
  totalCount: 0
}

type PaginationProps = {
  pagination?: Omit<ListMeta, 'facets'>
  queryKey?: string
  pathname: string
  searchParams: SearchParams
}

const activePageOffset = 2

const getPaginationPageNumbers = (
  pageCount: number,
  activePage: number
): number[] => {
  return Array(pageCount)
    .fill(0)
    .map((v, i) => i + 1)
    .filter((i) => {
      const activeIndex = activePage
      // Dont show first and last 2 page numbers and show only 1 pages around active
      if (
        i > 2 &&
        i < pageCount - 1 &&
        i >= activeIndex - activePageOffset &&
        i <= activeIndex + activePageOffset
      ) {
        return true
      } else return false
    })
}

// TODO @Patricia Responsive
const Pagination: ComponentType<PaginationProps> = ({
  pagination,
  pathname,
  searchParams
}) => {
  if (!pagination || !pagination.pageCount || pagination.pageCount === 1) {
    return null
  }
  const { page, pageCount } = pagination
  const next = page === pageCount ? null : page + 1
  const prev = page === 1 ? null : page + -1

  const PaginationNumberItem: ComponentType<{
    active?: boolean
    disabled?: boolean
    pageNumber: number
    children?: ReactNode
  }> = ({ active, disabled, pageNumber, children }) => (
    <PaginationItem
      disabled={disabled}
      active={active !== false && page === pageNumber}
      href={urlFor(pathname, searchParams, 'page', pageNumber.toString())}
    >
      <span>{children || pageNumber}</span>
    </PaginationItem>
  )

  const otherPages = getPaginationPageNumbers(pageCount, page)

  return (
    <div className="pagination">
      <PaginationNumberItem active={false} disabled={!prev} pageNumber={1}>
        <ChevronLeft size="1.3em" />
      </PaginationNumberItem>
      {pageCount <= 9 ? (
        [...Array(pageCount)].map((v: undefined, i: number) => (
          <PaginationNumberItem key={i} pageNumber={i + 1} />
        ))
      ) : (
        <>
          <PaginationNumberItem pageNumber={1} />
          <PaginationNumberItem pageNumber={2} />
          {(otherPages.length === 0 || otherPages[0] !== 3) && (
            <PaginationItem disabled>...</PaginationItem>
          )}
          {otherPages.map((i) => (
            <PaginationNumberItem key={i} pageNumber={i} />
          ))}
          {otherPages.length > 0 &&
            otherPages[otherPages.length - 1] !== pageCount - 2 && (
              <PaginationItem disabled>...</PaginationItem>
            )}
          <PaginationNumberItem pageNumber={pageCount - 1} />
          <PaginationNumberItem pageNumber={pageCount} />
        </>
      )}

      <PaginationNumberItem active={false} disabled={!next} pageNumber={1}>
        <ChevronRight size="1.3em" />
      </PaginationNumberItem>
    </div>
  )
}
export default Pagination
